import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useSubscription } from './useSubscription';
import { useUserId } from './useUserId';
import { TIER_LIMITS, type TierName, type TierLimits } from '@/constants/tierLimits';
import { resolveTier } from '@/lib/subscription';

interface ProfileChangeCounts {
  inspirationAdds: number;
  competitorAdds: number;
  windowStart: string | null;
  windowExpired: boolean;
}

const LOCKOUT_LIMITS: TierLimits = {
  maxInspirationSources: 0,
  maxCompetitors: 0,
  monthlyInspirationAdds: 0,
  monthlyCompetitorAdds: 0,
  monthlyBasicGenerations: 0,
  monthlyEnhancedGenerations: 0,
};

export interface UseTierLimitsReturn {
  tier: TierName | null;
  limits: TierLimits;
  changeCounts: ProfileChangeCounts;
  canAddInspiration: (currentCount: number) => boolean;
  canAddCompetitor: (currentCount: number) => boolean;
  remainingInspirationAdds: number;
  remainingCompetitorAdds: number;
  refresh: () => Promise<void>;
  loading: boolean;
}

const isWindowExpired = (windowStart: string): boolean => {
  const start = new Date(windowStart);
  const now = new Date();
  const diffMs = now.getTime() - start.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  return diffDays > 31;
};

export const useTierLimits = (): UseTierLimitsReturn => {
  const userId = useUserId();
  const {
    subscribed,
    productId,
    subscriptionStatus,
    subscriptionTrialEnd,
    subscriptionGracePeriodEnd,
    loading: subLoading,
  } = useSubscription();
  const [changeCounts, setChangeCounts] = useState<ProfileChangeCounts>({
    inspirationAdds: 0,
    competitorAdds: 0,
    windowStart: null,
    windowExpired: false,
  });
  const [loading, setLoading] = useState(true);

  // null tier = lockout. The route guard prevents non-paying users from reaching
  // this hook, so this branch is defensive — zero limits make any add fail closed.
  const tier = resolveTier({
    subscribed,
    status: subscriptionStatus,
    productId,
    subscriptionTrialEnd,
    gracePeriodEnd: subscriptionGracePeriodEnd,
  });
  const limits = tier ? TIER_LIMITS[tier] : LOCKOUT_LIMITS;

  const fetchChangeCounts = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    try {
      const { data, error } = await supabase
        .from('profile_change_counts')
        .select('*')
        .eq('user_id', userId)
        .order('window_start', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Failed to fetch profile change counts:', error);
        setLoading(false);
        return;
      }

      if (!data || isWindowExpired(data.window_start)) {
        setChangeCounts({
          inspirationAdds: 0,
          competitorAdds: 0,
          windowStart: data?.window_start || null,
          windowExpired: true,
        });
      } else {
        setChangeCounts({
          inspirationAdds: data.inspiration_adds || 0,
          competitorAdds: data.competitor_adds || 0,
          windowStart: data.window_start,
          windowExpired: false,
        });
      }
    } catch (err) {
      console.error('Error fetching change counts:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!subLoading) {
      fetchChangeCounts();
    }
  }, [userId, subLoading, fetchChangeCounts]);

  const canAddInspiration = useCallback(
    (currentCount: number) => {
      const slotOk = currentCount < limits.maxInspirationSources;
      const monthlyOk = changeCounts.inspirationAdds < limits.monthlyInspirationAdds;
      return slotOk && monthlyOk;
    },
    [limits, changeCounts]
  );

  const canAddCompetitor = useCallback(
    (currentCount: number) => {
      const slotOk = currentCount < limits.maxCompetitors;
      const monthlyOk = changeCounts.competitorAdds < limits.monthlyCompetitorAdds;
      return slotOk && monthlyOk;
    },
    [limits, changeCounts]
  );

  const remainingInspirationAdds = useMemo(
    () => Math.max(0, limits.monthlyInspirationAdds - changeCounts.inspirationAdds),
    [limits, changeCounts]
  );

  const remainingCompetitorAdds = useMemo(
    () => Math.max(0, limits.monthlyCompetitorAdds - changeCounts.competitorAdds),
    [limits, changeCounts]
  );

  return {
    tier,
    limits,
    changeCounts,
    canAddInspiration,
    canAddCompetitor,
    remainingInspirationAdds,
    remainingCompetitorAdds,
    refresh: fetchChangeCounts,
    loading: loading || subLoading,
  };
};
